import { FadeIn } from "@/components/Reveal";
import ChapterHeader from "@/components/ChapterHeader";
import UploadedPhotos from "@/components/UploadedPhotos";
import { useCategoryPhotos, pickPhoto } from "@/lib/usePhotos";

const MENUS = [
  { title: "Tandoor", items: ["Bhatti ke Murgh", "Murgh Malai Tikka", "Paneer Tikka"] },
  { title: "Continental", items: ["Pizza & Pasta (veg & non-veg)", "Chicken Stroganoff", "Steaks (veg & non-veg)"] },
  { title: "Chinese", items: ["Chilli Garlic Noodles", "Chilly Chicken", "Rice & Noodles (veg & non-veg)"] },
  { title: "Desserts", items: ["Brownie with ice cream", "Mango & cream", "Bread butter pudding"] },
];

export default function Dining() {
  const diningPhotos = useCategoryPhotos("dining");
  const tandoorImg = pickPhoto(diningPhotos, 0);
  return (
    <main data-testid="dining-page" className="pt-32 pb-24">
      <section className="mx-auto max-w-[1600px] px-6 lg:px-12">
        <FadeIn>
          <div className="stamp mb-8">Vol · III · Table</div>
          <h1 className="font-display text-6xl md:text-8xl leading-[0.95] text-[#F2E7D0] max-w-5xl">
            From soil to <em className="italic text-[#D97645]">plate</em> in a single morning.
          </h1>
        </FadeIn>
      </section>

      <section className="mx-auto max-w-[1600px] px-6 lg:px-12 py-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <FadeIn className="lg:col-span-6">
          <div className="w-full aspect-[4/5] bg-[#27221C] overflow-hidden">
            {tandoorImg && <img src={tandoorImg} alt="Tandoor" className="w-full h-full object-cover vintage-img" />}
          </div>
        </FadeIn>
        <FadeIn delay={0.15} className="lg:col-span-6">
          <ChapterHeader num="01" kicker="The Kitchen" title="Indian, Tandoor, Chinese and Continental — since the 1930's." />
          <p className="text-[#B8A891] leading-relaxed text-lg">
            Vegetables, dairy and seasonal fruits are grown and produced on the estate. The
            same fireplace that warmed British hunters in the 1930's still warms the dining
            room today.
          </p>
        </FadeIn>
      </section>

      <section className="bg-[#27221C] py-24">
        <div className="mx-auto max-w-[1600px] px-6 lg:px-12">
          <div className="chapter-num mb-4">The Menu · Selections</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
            {MENUS.map((m, i) => (
              <FadeIn key={m.title} delay={i * 0.08}>
                <div className="border-t border-white/15 pt-6" data-testid={`menu-${m.title.toLowerCase()}`}>
                  <div className="font-display text-3xl text-[#F2E7D0]">{m.title}</div>
                  <ul className="mt-4 space-y-3">
                    {m.items.map((it) => (
                      <li key={it} className="text-[#B8A891] text-sm">{it}</li>
                    ))}
                  </ul>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#1C1913] border-t border-[#3A3229] py-24 md:py-32" data-testid="dining-venues">
        <div className="mx-auto max-w-[1600px] px-6 lg:px-12">
          <div className="chapter-num mb-4">Where you'll dine</div>
          <h2 className="font-display text-5xl md:text-7xl leading-[0.95] text-[#F2E7D0] max-w-4xl mb-14">
            Three <em className="italic text-[#D97645]">places</em>. One estate.
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-stretch">
            {/* 1. SUNSHINE KITCHEN */}
            <FadeIn>
              <article
                data-testid="venue-sunshine-kitchen"
                className="border border-[#3A3229] bg-[#27221C] p-8 md:p-10 h-full flex flex-col"
              >
                <div className="font-mono-label text-[#B8A891] mb-4">Venue · 01</div>
                <h3 className="font-display text-4xl md:text-[2.75rem] leading-[1.05] text-[#F2E7D0] min-h-[5.5rem]">
                  Sunshine<br />
                  <em className="italic text-[#D97645]">Kitchen</em>
                </h3>
                <div className="mt-4 font-mono-label text-[#D97645] text-[11px] tracking-[0.28em] min-h-[1.5rem]">
                  THE À LA CARTE RESTAURANT
                </div>
                <p className="mt-6 font-display italic text-xl md:text-2xl text-[#F2E7D0]/85 leading-snug min-h-[4rem]">
                  “All day à la carte, in an old-world ambience.”
                </p>
                <p className="mt-6 text-[#B8A891] text-sm leading-relaxed flex-1">
                  Our full-menu restaurant — Indian, Tandoor, Chinese and Continental —
                  served through the day with sunlight, orchard views, and a menu that
                  changes with the season.
                </p>
                <div className="mt-8 pt-6 border-t border-[#3A3229] font-mono-label text-[10px] tracking-[0.28em] text-[#B8A891]/80">
                  BREAKFAST · LUNCH · SUPPER
                </div>
              </article>
            </FadeIn>

            {/* 2. BABLU DA THEKA */}
            <FadeIn delay={0.08}>
              <article
                data-testid="venue-bar"
                className="border border-[#3A3229] bg-[#27221C] p-8 md:p-10 h-full flex flex-col"
              >
                <div className="font-mono-label text-[#B8A891] mb-4">Venue · 02</div>
                <h3 className="font-display text-4xl md:text-[2.75rem] leading-[1.05] text-[#F2E7D0] min-h-[5.5rem]">
                  Bablu Da<br />
                  <em className="italic text-[#D97645]">Theka</em>
                </h3>
                <div className="mt-4 font-mono-label text-[#D97645] text-[11px] tracking-[0.28em] min-h-[1.5rem]">
                  THE BAR
                </div>
                <p className="mt-6 font-display italic text-xl md:text-2xl text-[#F2E7D0]/85 leading-snug min-h-[4rem]">
                  “Story by story, peg by peg.”
                </p>
                <p className="mt-6 text-[#B8A891] text-sm leading-relaxed flex-1">
                  A vintage estate bar with more stories than bottles — the bar tender
                  holds court behind the counter, pouring whisky, gin and slow evenings
                  well into the night.
                </p>
                <div className="mt-8 pt-6 border-t border-[#3A3229] font-mono-label text-[10px] tracking-[0.28em] text-[#B8A891]/80">
                  WHISKY · GIN · WINE · COCKTAILS
                </div>
              </article>
            </FadeIn>

            {/* 3. HERITAGE HALL */}
            <FadeIn delay={0.16}>
              <article
                data-testid="venue-heritage-hall"
                className="border border-[#3A3229] bg-[#27221C] p-8 md:p-10 h-full flex flex-col"
              >
                <div className="font-mono-label text-[#B8A891] mb-4">Venue · 03</div>
                <h3 className="font-display text-4xl md:text-[2.75rem] leading-[1.05] text-[#F2E7D0] min-h-[5.5rem]">
                  Heritage<br />
                  <em className="italic text-[#D97645]">Hall</em>
                </h3>
                <div className="mt-4 font-mono-label text-[#D97645] text-[11px] tracking-[0.28em] min-h-[1.5rem]">
                  PRIVATE HERITAGE DINING
                </div>
                <p className="mt-6 font-display italic text-xl md:text-2xl text-[#F2E7D0]/85 leading-snug min-h-[4rem]">
                  “A private dinner inside a 1930's home.”
                </p>
                <p className="mt-6 text-[#B8A891] text-sm leading-relaxed flex-1">
                  A curated heritage dinner — a quiet, premium evening in a house built
                  as a British hunting lodge, surrounded by trees, some going back
                  hundreds of years.
                </p>
                <div className="mt-8 pt-6 border-t border-[#3A3229] font-mono-label text-[10px] tracking-[0.28em] text-[#B8A891]/80">
                  PRIOR BOOKING · CUSTOMISED
                </div>
              </article>
            </FadeIn>
          </div>
        </div>
      </section>

      <UploadedPhotos category="dining" kicker="On The Table" title="From the Sunshine Kitchen, the tandoor, the dining hall." />
      <UploadedPhotos category="bar" kicker="Behind The Bar" title="Bablu Da Theka — an estate bar with more stories than bottles." />
      <UploadedPhotos category="heritage_hall" kicker="Heritage Hall" title="Private dinners inside the 1930's home, or on the lawn under the stars." />
    </main>
  );
}
